/** AI 水印浏览器适配：图片加载与 Canvas 合成；导入时不访问 Image/document，支持 SSR */
import { DESIGNKIT_AI_WATERMARK } from './assets';
import { getAIWatermarkLayout } from './layout';
import type { AIWatermarkAsset, AIWatermarkLayoutOptions, AIWatermarkOriginImage, AIWatermarkRect } from './types';

const watermarkImages = new Map<string, Promise<HTMLImageElement>>();

/** Canvas 绘制选项；imageRect 用于滤镜等带额外留白的画布，坐标以原图区域为准 */
export type AIWatermarkCanvasOptions = AIWatermarkLayoutOptions & {
    opacity?: number;
    imageRect?: AIWatermarkRect;
};

/**
 * 加载原图，保留已有图片元素的显示尺寸；跨域设置早于请求发起
 * @param originImage - URL、带 src 的对象或已加载图片元素
 * @returns 可用于尺寸计算及 Canvas 绘制的图片
 */
export async function loadAIWatermarkOriginImage(originImage: AIWatermarkOriginImage): Promise<HTMLImageElement> {
    if (typeof HTMLImageElement !== 'undefined' && originImage instanceof HTMLImageElement) return originImage;
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('水印相关图片加载失败'));
        image.src = typeof originImage === 'string' ? originImage : originImage.src;
    });
}

/**
 * 按素材 URL 缓存加载任务，失败后允许重试，不缓存业务原图
 * @param url - 水印素材地址
 * @returns 素材图片加载任务
 */
export function loadAIWatermarkAsset(url: string): Promise<HTMLImageElement> {
    let promise = watermarkImages.get(url);
    if (!promise) {
        promise = loadAIWatermarkOriginImage(url).catch((error) => {
            watermarkImages.delete(url);
            throw error;
        });
        watermarkImages.set(url, promise);
    }
    return promise;
}

/**
 * 在现有 Canvas 上叠加水印，可见内容使用同一份裁剪与几何规则，结束后恢复 context 状态
 * @param canvas - 已绘制原图、尺寸等于最终输出的画布
 * @param asset - 水印素材，默认设计室素材
 * @param options - 不透明度及兼容旧入口所需的几何参数
 * @returns 本次可见水印矩形
 */
export async function drawAIWatermarkOnCanvas(
    canvas: HTMLCanvasElement,
    asset: AIWatermarkAsset = DESIGNKIT_AI_WATERMARK,
    options: AIWatermarkCanvasOptions = {},
) {
    const image = await loadAIWatermarkAsset(asset.url);
    return drawLoadedAIWatermarkOnCanvas(canvas, image, asset, options);
}

/**
 * 使用已加载素材同步绘制，供视频帧和编辑器缓存画布复用同一套裁剪与尺寸规则
 * @param canvas - 输出画布；会在其现有内容上叠加水印并恢复 context 状态
 * @param image - 已加载的水印素材
 * @param asset - 与素材匹配的原始尺寸和可见内容边界
 * @param options - 不透明度、图片实际区域及可选定位参数
 * @returns 可见水印在输出画布内的矩形
 */
export function drawLoadedAIWatermarkOnCanvas(
    canvas: HTMLCanvasElement,
    image: HTMLImageElement,
    asset: AIWatermarkAsset,
    options: AIWatermarkCanvasOptions = {},
) {
    const imageRect = options.imageRect ?? { x: 0, y: 0, width: canvas.width, height: canvas.height };
    const layout = getAIWatermarkLayout(imageRect, asset, options);
    layout.x += imageRect.x;
    layout.y += imageRect.y;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('无法创建水印 Canvas context');
    const { opacity = asset.opacity ?? 0.8 } = options;
    if (!Number.isFinite(opacity) || opacity < 0 || opacity > 1) throw new Error('水印不透明度应在 [0, 1] 范围内');
    const source = asset.contentRect;
    context.save();
    try {
        context.globalAlpha *= opacity;
        context.drawImage(
            image,
            source.x,
            source.y,
            source.width,
            source.height,
            layout.x,
            layout.y,
            layout.width,
            layout.height,
        );
    } finally {
        context.restore();
    }
    return layout;
}
